import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Plus,
  TimerReset,
} from "lucide-react";

interface ProjectDetail {
  id: string;
  clientId: string;
  contractId: string | null;
  budgetId: string | null;
  name: string;
  description: string | null;
  status: string;
  ownerUserId: string;
  portalVisible: boolean;
  startDate: string | null;
  targetEndDate: string | null;
  clientName: string;
  columns: Array<{ id: string; key: "todo" | "in_progress" | "review" | "done"; name: string; colorHex: string | null; sortOrder: number }>;
  tasks: Array<{
    id: string;
    projectId: string;
    serviceItemId: string | null;
    title: string;
    description: string | null;
    status: string;
    columnId: string;
    assigneeUserId: string | null;
    dueDate: string | null;
    estimateMinutes: number | null;
    position: number;
    columnName: string;
    assigneeName: string | null;
    assigneeEmail: string | null;
    serviceItemName: string | null;
  }>;
}

interface ProjectOptionsResponse {
  staff: Array<{ id: string; name: string; email: string; role: string }>;
  clients: Array<{ id: string; name: string; accountManagerUserId: string | null }>;
  contracts: Array<{ id: string; clientId: string; title: string | null; status: string }>;
  budgets: Array<{ id: string; contractId: string; name: string; status: string }>;
  serviceItems: Array<{ id: string; budgetId: string; name: string; requiresTask: boolean; isActive: boolean }>;
}

interface TaskFormState {
  title: string;
  description: string;
  serviceItemId: string | null;
  status: "todo" | "in_progress" | "review" | "done" | "blocked";
  columnId: string;
  assigneeUserId: string | null;
  dueDate: string;
  estimateMinutes: string;
}

const emptyTaskForm: TaskFormState = {
  title: "",
  description: "",
  serviceItemId: null,
  status: "todo",
  columnId: "",
  assigneeUserId: null,
  dueDate: "",
  estimateMinutes: "",
};

function toDateInput(value: string | null) {
  return value ? value.slice(0, 10) : "";
}

function taskCanReceiveTime(task: { serviceItemId: string | null }) {
  return Boolean(task.serviceItemId);
}

export default function ProjectDetailPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/projects/:id");
  const projectId = params?.id;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [taskForm, setTaskForm] = useState<TaskFormState>(emptyTaskForm);

  const { data: project, isLoading } = useQuery<ProjectDetail>({
    queryKey: [`/api/projects/${projectId}`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: Boolean(projectId),
  });

  const { data: options } = useQuery<ProjectOptionsResponse>({
    queryKey: ["/api/projects/meta/options"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const tasksByColumn = useMemo(() => {
    const map = new Map<string, ProjectDetail["tasks"]>();
    for (const task of project?.tasks ?? []) {
      const rows = map.get(task.columnId) ?? [];
      rows.push(task);
      map.set(task.columnId, rows);
    }
    return map;
  }, [project]);

  const createTaskMutation = useMutation({
    mutationFn: async (payload: TaskFormState) => {
      const response = await apiRequest("POST", `/api/projects/${projectId}/tasks`, {
        ...payload,
        serviceItemId: payload.serviceItemId,
        assigneeUserId: payload.assigneeUserId,
        dueDate: payload.dueDate || null,
        estimateMinutes: payload.estimateMinutes ? Number(payload.estimateMinutes) : null,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
      setSheetOpen(false);
      setEditingTaskId(null);
      setTaskForm(emptyTaskForm);
      toast({ title: "Task created" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create task",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: async (payload: TaskFormState) => {
      const response = await apiRequest("PATCH", `/api/project-tasks/${editingTaskId}`, {
        ...payload,
        serviceItemId: payload.serviceItemId,
        assigneeUserId: payload.assigneeUserId,
        dueDate: payload.dueDate || null,
        estimateMinutes: payload.estimateMinutes ? Number(payload.estimateMinutes) : null,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
      setSheetOpen(false);
      setEditingTaskId(null);
      setTaskForm(emptyTaskForm);
      toast({ title: "Task updated" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update task",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const moveTaskMutation = useMutation({
    mutationFn: async ({ taskId, columnId }: { taskId: string; columnId: string }) => {
      const response = await apiRequest("POST", `/api/project-tasks/${taskId}/move`, { columnId });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to move task",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  function openCreateTask(columnId: string) {
    setEditingTaskId(null);
    setTaskForm({
      ...emptyTaskForm,
      columnId,
      status: columnId === project?.columns.find((column) => column.key === "done")?.id ? "done" : "todo",
    });
    setSheetOpen(true);
  }

  function openEditTask(taskId: string) {
    const task = project?.tasks.find((item) => item.id === taskId);
    if (!task) return;

    setEditingTaskId(task.id);
    setTaskForm({
      title: task.title,
      description: task.description ?? "",
      serviceItemId: task.serviceItemId,
      status: task.status as TaskFormState["status"],
      columnId: task.columnId,
      assigneeUserId: task.assigneeUserId,
      dueDate: toDateInput(task.dueDate),
      estimateMinutes: task.estimateMinutes ? String(task.estimateMinutes) : "",
    });
    setSheetOpen(true);
  }

  function handleTaskSubmit() {
    if (editingTaskId) {
      updateTaskMutation.mutate(taskForm);
      return;
    }

    createTaskMutation.mutate(taskForm);
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-[180px] rounded-lg" />
        <Skeleton className="h-[360px] rounded-lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/projects")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to projects
        </Button>
        <p className="text-muted-foreground">Project not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/projects")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-heading">{project.name}</h1>
            <p className="text-sm text-muted-foreground">
              {project.clientName} · {project.status}
            </p>
          </div>
        </div>
        <Badge variant={project.portalVisible ? "default" : "secondary"}>
          {project.portalVisible ? "Visible in portal" : "Hidden from portal"}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Project summary</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Client</p>
            <p className="font-medium">{project.clientName}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Start</p>
            <p className="font-medium">{project.startDate ? toDateInput(project.startDate) : "Not set"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Target end</p>
            <p className="font-medium">{project.targetEndDate ? toDateInput(project.targetEndDate) : "Not set"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Tasks</p>
            <p className="font-medium">{project.tasks.length}</p>
          </div>
          {project.description && (
            <div className="md:col-span-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Description</p>
              <p className="mt-1 text-sm text-muted-foreground">{project.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-4">
        {project.columns.map((column, columnIndex) => (
          <Card key={column.id} className="min-h-[420px]">
            <CardHeader className="space-y-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{column.name}</CardTitle>
                <Badge variant="outline">{tasksByColumn.get(column.id)?.length ?? 0}</Badge>
              </div>
              <Button size="sm" variant="outline" onClick={() => openCreateTask(column.id)}>
                <Plus className="mr-2 h-4 w-4" />
                Add task
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {(tasksByColumn.get(column.id) ?? []).map((task) => (
                <div
                  key={task.id}
                  className="rounded-lg border p-3 transition-colors hover:border-primary/50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-2">
                      <button
                        className="text-left font-medium hover:text-primary"
                        onClick={() => openEditTask(task.id)}
                      >
                        {task.title}
                      </button>
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <p>{task.serviceItemName ? `Service item: ${task.serviceItemName}` : "No service item linked yet"}</p>
                        <p>{task.assigneeName ? `Assignee: ${task.assigneeName}` : "Unassigned"}</p>
                        <p>{task.dueDate ? `Due: ${toDateInput(task.dueDate)}` : "No due date"}</p>
                      </div>
                    </div>
                    <Badge variant={taskCanReceiveTime({ serviceItemId: task.serviceItemId }) ? "secondary" : "destructive"}>
                      {taskCanReceiveTime({ serviceItemId: task.serviceItemId }) ? "Time-ready" : "Blocked"}
                    </Badge>
                  </div>

                  {task.description && (
                    <p className="mt-2 text-sm text-muted-foreground">{task.description}</p>
                  )}

                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <TimerReset className="h-3.5 w-3.5" />
                      {task.estimateMinutes ? `${task.estimateMinutes} min` : "No estimate"}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={columnIndex === 0 || moveTaskMutation.isPending}
                        onClick={() =>
                          moveTaskMutation.mutate({
                            taskId: task.id,
                            columnId: project.columns[columnIndex - 1].id,
                          })
                        }
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={
                          columnIndex === project.columns.length - 1 || moveTaskMutation.isPending
                        }
                        onClick={() =>
                          moveTaskMutation.mutate({
                            taskId: task.id,
                            columnId: project.columns[columnIndex + 1].id,
                          })
                        }
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{editingTaskId ? "Edit task" : "Create task"}</SheetTitle>
            <SheetDescription>
              Tasks can exist for planning without a service item, but they cannot receive time until one is assigned.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="task-title">Title</Label>
              <Input
                id="task-title"
                value={taskForm.title}
                onChange={(event) => setTaskForm((current) => ({ ...current, title: event.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                rows={5}
                value={taskForm.description}
                onChange={(event) =>
                  setTaskForm((current) => ({ ...current, description: event.target.value }))
                }
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Column</Label>
                <Select
                  value={taskForm.columnId || undefined}
                  onValueChange={(value) => setTaskForm((current) => ({ ...current, columnId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {project.columns.map((column) => (
                      <SelectItem key={column.id} value={column.id}>
                        {column.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={taskForm.status}
                  onValueChange={(value: TaskFormState["status"]) =>
                    setTaskForm((current) => ({ ...current, status: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo">To do</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Service item</Label>
                <Select
                  value={taskForm.serviceItemId ?? "none"}
                  onValueChange={(value) =>
                    setTaskForm((current) => ({ ...current, serviceItemId: value === "none" ? null : value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Optional service item" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No service item yet</SelectItem>
                    {(options?.serviceItems ?? []).map((serviceItem) => (
                      <SelectItem key={serviceItem.id} value={serviceItem.id}>
                        {serviceItem.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Assignee</Label>
                <Select
                  value={taskForm.assigneeUserId ?? "none"}
                  onValueChange={(value) =>
                    setTaskForm((current) => ({ ...current, assigneeUserId: value === "none" ? null : value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Optional assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {(options?.staff ?? []).map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="task-due-date">Due date</Label>
                <Input
                  id="task-due-date"
                  type="date"
                  value={taskForm.dueDate}
                  onChange={(event) => setTaskForm((current) => ({ ...current, dueDate: event.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="task-estimate">Estimate minutes</Label>
                <Input
                  id="task-estimate"
                  type="number"
                  min="0"
                  value={taskForm.estimateMinutes}
                  onChange={(event) =>
                    setTaskForm((current) => ({ ...current, estimateMinutes: event.target.value }))
                  }
                />
              </div>
            </div>

            <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
              {taskForm.serviceItemId
                ? "This task can receive time entries because a service item is assigned."
                : "This task is still planning-only. Assign a service item before anyone logs time to it."}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSheetOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleTaskSubmit}
                disabled={
                  !taskForm.title.trim() ||
                  !taskForm.columnId ||
                  createTaskMutation.isPending ||
                  updateTaskMutation.isPending
                }
              >
                {editingTaskId ? "Save task" : "Create task"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
